import { useState, useEffect, useRef } from 'react';
import {
  MessageCircle, Mail, Send, AlertTriangle, CheckCircle,
  XCircle, ChevronRight, LayoutTemplate, Loader2, ChevronDown, ChevronUp,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { bulkMessageService, templateService } from '../../services/api';
import Modal from '../../components/ui/Modal';

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: '#25D366' },
  { key: 'email',    label: 'Email',    icon: Mail,          color: '#3B82F6' },
];

const STEP_CHANNEL  = 'channel';
const STEP_TEMPLATE = 'template';
const STEP_CONFIRM  = 'confirm';
const STEP_PROGRESS = 'progress';

const DONE_STATUSES = ['completed', 'completed_with_errors', 'failed'];

const BulkConnectModal = ({ open, onClose, leads }) => {
  const { dark } = useTheme();

  const [step, setStep]               = useState(STEP_CHANNEL);
  const [channel, setChannel]         = useState('whatsapp');
  const [templates, setTemplates]     = useState([]);
  const [tmplLoading, setTmplLoading] = useState(false);
  const [selected, setSelected]       = useState(null);
  const [sending, setSending]         = useState(false);
  const [startError, setStartError]   = useState(null);

  const [campaign, setCampaign]           = useState(null);
  const [startResult, setStartResult]     = useState(null); // { skipped: [...] } from the start response
  const [showFailures, setShowFailures]   = useState(false);
  const [failures, setFailures]           = useState([]);
  const [failuresLoading, setFailuresLoading] = useState(false);

  const pollTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setStep(STEP_CHANNEL);
      setChannel('whatsapp');
      setTemplates([]);
      setSelected(null);
      setSending(false);
      setStartError(null);
      setCampaign(null);
      setStartResult(null);
      setShowFailures(false);
      setFailures([]);
      clearInterval(pollTimer.current);
    }
  }, [open]);

  useEffect(() => () => clearInterval(pollTimer.current), []);

  if (!leads || leads.length === 0) return null;

  const withMobile = leads.filter(l => l.mobile);
  const withEmail  = leads.filter(l => l.email);
  const sendableFor = (ch) => (ch === 'whatsapp' ? withMobile : withEmail);
  const canUse = sendableFor(channel).length > 0;
  const ch = CHANNELS.find(c => c.key === channel);
  const skippedForChannel = leads.filter(l => !(channel === 'whatsapp' ? l.mobile : l.email));

  // ── Step handlers ───────────────────────────────────────────────────────────

  const goToTemplates = async () => {
    setTmplLoading(true);
    try {
      const { data } = await templateService.getAll({ type: channel });
      setTemplates(data.data || data || []);
    } catch {
      setTemplates([]);
    } finally {
      setTmplLoading(false);
    }
    setSelected(null);
    setStep(STEP_TEMPLATE);
  };

  const selectTemplate = (tmpl) => {
    setSelected(tmpl);
    setStep(STEP_CONFIRM);
  };

  const startPolling = (campaignId) => {
    clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const { data } = await bulkMessageService.getCampaign(campaignId);
        const c = data.data || data;
        setCampaign(c);
        if (DONE_STATUSES.includes(c.status)) clearInterval(pollTimer.current);
      } catch {
        clearInterval(pollTimer.current);
      }
    }, 2000);
  };

  const handleSend = async () => {
    setSending(true);
    setStartError(null);
    try {
      const leadIds = leads.map(l => l._id);
      const { data } = await bulkMessageService.start(leadIds, channel, selected._id);
      const result = data.data || data;
      setStartResult(result);
      setCampaign({
        _id: result.campaignId,
        totalCount: result.totalCount,
        sentCount: 0,
        failedCount: 0,
        skippedCount: result.skippedCount,
        status: 'queued',
      });
      setStep(STEP_PROGRESS);
      startPolling(result.campaignId);
    } catch (err) {
      setStartError(err.response?.data?.message || 'Failed to start bulk send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const loadFailures = async () => {
    setShowFailures(v => !v);
    if (failures.length > 0 || !campaign?._id) return;
    setFailuresLoading(true);
    try {
      const { data } = await bulkMessageService.getCampaignItems(campaign._id, { status: 'failed', limit: 100 });
      setFailures(data.data || []);
    } catch {
      setFailures([]);
    } finally {
      setFailuresLoading(false);
    }
  };

  const handleClose = () => {
    clearInterval(pollTimer.current);
    onClose();
  };

  // ── Theme shortcuts ─────────────────────────────────────────────────────────
  const text   = dark ? '#F1F5F9' : '#0F172A';
  const sub    = dark ? '#94A3B8' : '#64748B';
  const border = dark ? '#334155' : '#E2E8F0';
  const surfBg = dark ? '#0F172A' : '#F8FAFC';

  const STEPS = [STEP_CHANNEL, STEP_TEMPLATE, STEP_CONFIRM];
  const stepLabels = { [STEP_CHANNEL]: 'Channel', [STEP_TEMPLATE]: 'Template', [STEP_CONFIRM]: 'Confirm & Send' };

  const isDone = campaign && DONE_STATUSES.includes(campaign.status);
  const processedCount = campaign ? campaign.sentCount + campaign.failedCount + campaign.skippedCount : 0;
  const progressPct = campaign?.totalCount ? Math.round((processedCount / campaign.totalCount) * 100) : 0;

  return (
    <Modal open={open} onClose={handleClose} title={`Send to ${leads.length} Lead${leads.length > 1 ? 's' : ''}`} width={620}>
      {/* Breadcrumb */}
      {step !== STEP_PROGRESS && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 20 }}>
          {STEPS.map((s, i) => {
            const idx = STEPS.indexOf(step);
            const isPast = i < idx;
            const isNow = i === idx;
            return (
              <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  onClick={() => isPast && setStep(s)}
                  style={{
                    fontSize: 12, fontWeight: isNow ? 700 : 500, border: 'none', background: 'none',
                    cursor: isPast ? 'pointer' : 'default',
                    color: isNow ? (dark ? '#93C5FD' : '#2563EB')
                      : isPast ? sub : dark ? '#475569' : '#CBD5E1',
                    padding: 0, textDecoration: isPast ? 'underline' : 'none',
                  }}
                >
                  {i + 1}. {stepLabels[s]}
                </button>
                {i < STEPS.length - 1 && <ChevronRight size={12} color={dark ? '#475569' : '#CBD5E1'} />}
              </span>
            );
          })}
        </div>
      )}

      {/* ── STEP 1: Channel ────────────────────────────────────────────────── */}
      {step === STEP_CHANNEL && (
        <>
          <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
            {CHANNELS.map(c => {
              const count = sendableFor(c.key).length;
              const available = count > 0;
              const isActive = channel === c.key;
              const Icon = c.icon;
              return (
                <button
                  key={c.key}
                  onClick={() => available && setChannel(c.key)}
                  title={!available ? `No selected leads have a ${c.key === 'whatsapp' ? 'mobile number' : 'email address'}` : ''}
                  style={{
                    flex: 1, padding: '18px 12px', borderRadius: 12, cursor: available ? 'pointer' : 'not-allowed',
                    border: `2px solid ${isActive ? c.color : border}`,
                    background: isActive ? (dark ? c.color + '18' : c.color + '0F') : surfBg,
                    opacity: available ? 1 : 0.4, transition: 'all 0.15s',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                  }}
                >
                  <Icon size={26} color={isActive ? c.color : sub} />
                  <span style={{ fontSize: 14, fontWeight: 600, color: isActive ? c.color : sub }}>{c.label}</span>
                  <span style={{ fontSize: 11, color: sub }}>{count} of {leads.length} leads</span>
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={goToTemplates}
              disabled={!canUse}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 9, border: 'none',
                background: canUse ? ch?.color : (dark ? '#334155' : '#E2E8F0'),
                color: canUse ? '#fff' : sub,
                fontSize: 14, fontWeight: 600, cursor: canUse ? 'pointer' : 'not-allowed',
              }}
            >
              Choose Template <ChevronRight size={15} />
            </button>
          </div>
        </>
      )}

      {/* ── STEP 2: Template picker ─────────────────────────────────────────── */}
      {step === STEP_TEMPLATE && (
        <>
          <div style={{ fontSize: 13, color: sub, marginBottom: 14 }}>
            Select a {ch?.label} template to send to <strong style={{ color: text }}>{sendableFor(channel).length} leads</strong>.
          </div>

          {tmplLoading ? (
            <div style={{ padding: '30px 0', textAlign: 'center', color: sub, fontSize: 13 }}>Loading templates…</div>
          ) : templates.length === 0 ? (
            <div style={{
              padding: '30px 20px', textAlign: 'center', borderRadius: 12,
              border: `1px dashed ${border}`, marginBottom: 20,
            }}>
              <LayoutTemplate size={32} color={sub} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 14, fontWeight: 600, color: text, marginBottom: 4 }}>No templates found</div>
              <div style={{ fontSize: 12, color: sub }}>
                Go to <strong>Templates</strong> in the sidebar to create {ch?.label} templates.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', marginBottom: 20 }}>
              {templates.map(tmpl => (
                <button
                  key={tmpl._id}
                  onClick={() => selectTemplate(tmpl)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '12px 14px', borderRadius: 10, border: `1px solid ${border}`,
                    background: surfBg, cursor: 'pointer', textAlign: 'left',
                    transition: 'border-color 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = ch?.color}
                  onMouseLeave={e => e.currentTarget.style.borderColor = border}
                >
                  <ch.icon size={18} color={ch?.color} style={{ flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: text }}>{tmpl.name}</div>
                    {tmpl.subject && (
                      <div style={{ fontSize: 12, color: sub, marginTop: 2 }}>Subject: {tmpl.subject}</div>
                    )}
                    <div style={{
                      fontSize: 12, color: sub, marginTop: 3,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {tmpl.body.slice(0, 90)}{tmpl.body.length > 90 ? '…' : ''}
                    </div>
                  </div>
                  <ChevronRight size={15} color={sub} style={{ flexShrink: 0 }} />
                </button>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <button
              onClick={() => setStep(STEP_CHANNEL)}
              style={{ fontSize: 13, color: sub, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ← Back
            </button>
          </div>
        </>
      )}

      {/* ── STEP 3: Confirm & Send ──────────────────────────────────────────── */}
      {step === STEP_CONFIRM && (
        <>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
            background: dark ? '#0F172A' : '#F0FDF4',
            border: `1px solid ${dark ? '#166534' : '#BBF7D0'}`, borderRadius: 8, marginBottom: 12,
          }}>
            <CheckCircle size={14} color={dark ? '#4ADE80' : '#16A34A'} />
            <span style={{ fontSize: 12, color: dark ? '#4ADE80' : '#15803D' }}>
              <strong>{sendableFor(channel).length}</strong> lead{sendableFor(channel).length > 1 ? 's' : ''} will receive this via {ch?.label}
            </span>
          </div>

          {skippedForChannel.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px',
              background: dark ? '#3F1D0A' : '#FFFBEB',
              border: `1px solid ${dark ? '#92400E' : '#FDE68A'}`, borderRadius: 8, marginBottom: 16,
            }}>
              <AlertTriangle size={14} color="#F59E0B" style={{ marginTop: 1, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: dark ? '#FBBF24' : '#92400E' }}>
                <strong>{skippedForChannel.length}</strong> lead{skippedForChannel.length > 1 ? 's' : ''} will be skipped
                (no {channel === 'whatsapp' ? 'mobile number' : 'email address'}):{' '}
                {skippedForChannel.slice(0, 5).map(l => l.fullName).join(', ')}
                {skippedForChannel.length > 5 ? ` +${skippedForChannel.length - 5} more` : ''}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12 }}>
            <LayoutTemplate size={13} color={sub} />
            <span style={{ fontSize: 12, color: sub }}>Template:</span>
            <span style={{
              fontSize: 12, fontWeight: 600, color: ch?.color,
              background: ch ? ch.color + '15' : 'transparent',
              borderRadius: 6, padding: '2px 8px',
            }}>
              {selected?.name}
            </span>
          </div>

          {channel === 'email' && selected?.subject && (
            <div style={{ marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: sub }}>Subject: </span>
              <span style={{ fontSize: 13, color: text }}>{selected.subject}</span>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: sub, marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.4 }}>
              Message Template (personalized per lead)
            </div>
            <div style={{
              background: surfBg, border: `1px solid ${border}`, borderRadius: 10,
              padding: 16, maxHeight: 220, overflowY: 'auto',
            }}>
              <pre style={{
                margin: 0, fontFamily: 'inherit', fontSize: 13, lineHeight: 1.65,
                color: dark ? '#CBD5E1' : '#334155', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
              }}>
                {selected?.body}
              </pre>
            </div>
          </div>

          {startError && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
              background: dark ? '#3F0A0A' : '#FEF2F2',
              border: `1px solid ${dark ? '#7F1D1D' : '#FECACA'}`, borderRadius: 8, marginBottom: 16,
            }}>
              <XCircle size={14} color="#EF4444" />
              <span style={{ fontSize: 12, color: dark ? '#FCA5A5' : '#B91C1C' }}>{startError}</span>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button
              onClick={() => setStep(STEP_TEMPLATE)}
              style={{ fontSize: 13, color: sub, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              ← Change Template
            </button>
            <button
              onClick={handleSend}
              disabled={sending}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '10px 22px', borderRadius: 9, border: 'none',
                background: ch?.color, color: '#fff',
                fontSize: 14, fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
                opacity: sending ? 0.75 : 1,
              }}
            >
              <Send size={14} />
              {sending ? 'Starting…' : `Send to ${sendableFor(channel).length} Leads`}
            </button>
          </div>
        </>
      )}

      {/* ── STEP 4: Progress ────────────────────────────────────────────────── */}
      {step === STEP_PROGRESS && campaign && (
        <div>
          <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
            {isDone ? (
              campaign.status === 'completed'
                ? <CheckCircle size={40} color="#22C55E" style={{ marginBottom: 10 }} />
                : <AlertTriangle size={40} color="#F59E0B" style={{ marginBottom: 10 }} />
            ) : (
              <Loader2 size={40} color={ch?.color} style={{ marginBottom: 10, animation: 'spin 1s linear infinite' }} />
            )}
            <div style={{ fontSize: 16, fontWeight: 600, color: text }}>
              {isDone
                ? (campaign.status === 'completed' ? 'All messages sent!' : 'Finished with some errors')
                : 'Sending messages…'}
            </div>
            <div style={{ fontSize: 12, color: sub, marginTop: 4 }}>
              {processedCount} of {campaign.totalCount} processed
            </div>
          </div>

          {/* Progress bar */}
          <div style={{
            height: 8, borderRadius: 4, background: dark ? '#1E293B' : '#E2E8F0',
            overflow: 'hidden', marginBottom: 20,
          }}>
            <div style={{
              height: '100%', width: `${progressPct}%`,
              background: campaign.failedCount > 0 ? 'linear-gradient(90deg,#22C55E,#F59E0B)' : '#22C55E',
              transition: 'width 0.3s',
            }} />
          </div>

          {/* Counts */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Sent', value: campaign.sentCount, color: '#22C55E' },
              { label: 'Failed', value: campaign.failedCount, color: '#EF4444' },
              { label: 'Skipped', value: campaign.skippedCount, color: '#F59E0B' },
              { label: 'Pending', value: Math.max(campaign.totalCount - processedCount, 0), color: sub },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, textAlign: 'center', padding: '10px 6px', borderRadius: 10,
                border: `1px solid ${border}`, background: surfBg,
              }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: sub }}>{s.label}</div>
              </div>
            ))}
          </div>

          {campaign.failedCount > 0 && (
            <div style={{ marginBottom: 16 }}>
              <button
                onClick={loadFailures}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
                  cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#EF4444', padding: 0,
                }}
              >
                {showFailures ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                View {campaign.failedCount} failure{campaign.failedCount > 1 ? 's' : ''}
              </button>
              {showFailures && (
                <div style={{
                  marginTop: 10, maxHeight: 200, overflowY: 'auto',
                  border: `1px solid ${border}`, borderRadius: 10,
                }}>
                  {failuresLoading ? (
                    <div style={{ padding: 16, textAlign: 'center', color: sub, fontSize: 13 }}>Loading…</div>
                  ) : failures.length === 0 ? (
                    <div style={{ padding: 16, textAlign: 'center', color: sub, fontSize: 13 }}>No details available</div>
                  ) : (
                    failures.map(f => (
                      <div key={f._id} style={{
                        padding: '10px 12px', borderBottom: `1px solid ${border}`,
                        display: 'flex', justifyContent: 'space-between', gap: 10,
                      }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: text }}>{f.leadName}</span>
                        <span style={{ fontSize: 12, color: '#EF4444', textAlign: 'right' }}>{f.error}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {!isDone && (
            <div style={{ fontSize: 12, color: sub, textAlign: 'center', marginBottom: 16 }}>
              You can close this window — sending will continue in the background.
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleClose}
              style={{
                padding: '10px 28px', borderRadius: 9, border: 'none',
                background: dark ? '#334155' : '#E2E8F0', color: text,
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Modal>
  );
};

export default BulkConnectModal;
